import { useEffect } from 'react';

/**
 * Change-summary toasts — the old Code Box's signature feedback surface, floating bottom-center
 * OVER the preview rather than in a chat transcript.
 *
 * Why keep this instead of a message log: it puts the result next to the thing that changed, and
 * each card carries its own undo. Two rules ported verbatim because they are what stop the surface
 * getting cluttered:
 *   - max 4 visible, newest first;
 *   - only "minor" toasts auto-dismiss (6s). Anything that ASKS the user something — an error, or a
 *     card offering undo — sticks until they deal with it.
 */

export type ToastKind = 'success' | 'info' | 'warning' | 'error';

export interface Toast {
  id: string;
  kind: ToastKind;
  title: string;
  lines?: string[];
  /** offering an action makes a toast sticky */
  onUndo?: () => void;
}

const ACCENT: Record<ToastKind, string> = {
  success: 'bg-success',
  info: 'bg-accent',
  warning: 'bg-danger/70',
  error: 'bg-danger',
};

const isSticky = (toast: Toast): boolean =>
  toast.kind === 'error' || toast.kind === 'warning' || Boolean(toast.onUndo);

export function ChangeToasts({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}) {
  useEffect(() => {
    const timers = toasts
      .filter(t => !isSticky(t))
      .map(t => window.setTimeout(() => onDismiss(t.id), 6000));
    return () => timers.forEach(window.clearTimeout);
  }, [toasts, onDismiss]);

  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none absolute bottom-5 left-1/2 z-30 w-[min(560px,calc(100%-32px))] -translate-x-1/2 space-y-2">
      {toasts.slice(0, 4).map(toast => (
        <div
          key={toast.id}
          className="anim-in pointer-events-auto overflow-hidden rounded-2xl border border-line bg-surface shadow-card"
        >
          <div className={['h-[3px] w-full', ACCENT[toast.kind]].join(' ')} />
          <div className="flex items-start gap-3 p-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">{toast.title}</p>
              {toast.lines && toast.lines.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {toast.lines.map((line, i) => (
                    <li
                      key={i}
                      className="anim-in text-xs text-muted"
                      style={{ animationDelay: `${i * 40}ms` }}
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              )}
              {toast.onUndo && (
                <button
                  type="button"
                  onClick={() => {
                    toast.onUndo?.();
                    onDismiss(toast.id);
                  }}
                  className="mt-2 rounded-lg border border-line px-2.5 py-1 text-xs font-semibold transition hover:bg-page"
                >
                  Undo this change
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              aria-label="Dismiss"
              className="shrink-0 text-muted transition hover:text-ink"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
